import { ConfirmSignInForm, InitiateSignInForm } from "../types/auth";
import { ChangePasswordForm } from "../pages/Account/AccountProfile/ChangePasswordModal";
import { useAuthStore } from "../store/useAuthStore";
import { useMessagesStore } from "../store/useMessagesStore";
import { AddAdminForm, AdminDashboardLayout, AdminListItem, AdminUserListItem, MeResponse, User, UserRole, UserRoleName } from "../types/user";
import api, { publicApi } from "./api";

const addError = useMessagesStore.getState().addError;
const { setAccessToken, setRefreshToken } = useAuthStore.getState();

export const apiInitiateSignIn = async (data: InitiateSignInForm) => {
  try {
    const result = await publicApi.post('/auth/sign-in/initiate?type=admin', data);
    // for dev only
    const otp = result?.data?.otp as string | undefined;
    return { success: true, otp };
  } catch (_err) {
    // @ts-ignore
    const message = _err.response?.data?.message;
    addError(message ?? `Something went wrong: login failed`);
    return { success: false };
  }
}

export const apiConfirmSignIn = async (data: ConfirmSignInForm) => {
  try {
    const res = await publicApi.post('/auth/sign-in/confirm', data);
    try {
      const { access_token, refresh_token } = res?.data ?? {};
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
    } catch {
      addError('Invalid token');
      return false;
    }
    return true;
  } catch (_err) {
    // @ts-ignore
    const message = _err.response?.data?.message;
    addError(message ?? `Something went wrong: login failed`);
    return false;
  }
}

export const apiSetAdminPassword = async (password: string, token: string) => {
  try {
    await publicApi.post("/auth/set-password", { password, token });
    return true;
  } catch (error) {
    console.error(error);
    addError('Failed to set password');
    return false;
  }
}

export const apiGetMe = async () => {
  try {
    const result = await api.get<MeResponse>("/auth/me");
    return result.data;
  } catch (error) {
    addError('Failed to get current user');
    console.error(error);
    return null;
  }
}

export const apiGetUserProfile = async () => {
  try {
    const result = await api.get<User>("/user/profile");
    return result.data;
  } catch (error) {
    addError('Failed to get user profile');
    console.error(error);
    return null;
  }
}

export const apiUpdateUserProfile = async (formData: FormData) => {
  try {
    const result = await api.put<User>("/user/profile", formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    return result.data;
  } catch (error) {
    addError('Failed to update user profile');
    console.error(error);
    return null;
  }
}

export const apiChangePassword = async (data: ChangePasswordForm) => {
  try {
    await api.post("/auth/change-password", data);
    return true;
  } catch (error) {
    addError('Failed to change password');
    console.error(error);
    return false;
  }
}

export const apiForgotPassword = async () => {
  try {
    await api.post("/auth/forgot-password");
    return true;
  } catch (error) {
    addError('Failed to send request to change password');
    console.error(error);
    return false;
  }
}

export const apiGetUsersList = async (brandId?: string) => {
  try {
    const params = new URLSearchParams();
    params.append("usersRoleName", "BRAND_ADMIN");
    params.append("getChildBrandAdmins", "true");
    if (brandId) {
      params.append("brandId", brandId);
    }

    const result = await api.get<AdminUserListItem[]>(`/user/list?${params}`);
    return result.data;
  } catch (error) {
    addError('Failed to get users list');
    console.error(error);
    return [];
  }
}

export const apiAddAdmin = async (form: AddAdminForm & { brandId: string }) => {
  try {
    const { role, ...rest } = form;
    await api.post(`/user/admin`, { ...rest, role: role ?? UserRoleName.BRAND_ADMIN });
    return true;
  } catch (error: any) {
    console.error(error);
    const errorMessage = error?.response?.data?.message || 'Failed to invite admin. Please try again.';
    addError(errorMessage);
    return false;
  }
}

export const apiGetAssignableRoles = async () => {
  try {
    const res = await api.get<UserRole[]>("/user/roles");
    return (res.data ?? []).filter(
      (r) => r.type === "ADMIN" && r.name !== ("CLIENT_DEFAULT" as UserRoleName)
    );
  } catch (error: any) {
    console.error(error);
    // A 403 means this admin's role is not permitted to create admins at all
    // (see editAdminGuard / getUserRoles on the API) - that's an expected
    // outcome, not a failure worth alarming the user about.
    if (error?.response?.status !== 403) {
      addError('Failed to load user roles');
    }
    return [];
  }
}

export const apiGetUserRole = async (id: number) => {
  try {
    const res = await api.get<UserRole>(`/user/role/${id}`);
    return res.data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export const apiGetAdminsList = async () => {
  try {
    const result = await api.get<AdminListItem[]>("/user/admins");
    return result.data;
  } catch (error) {
    addError('Failed to get admins list');
    console.error(error);
    return [];
  }
}

export const apiRemoveAdmin = async (adminId: string) => {
  try {
    await api.delete(`/user/admin/${adminId}`);
    return true;
  } catch (error: any) {
    console.error(error);
    const errorMessage = error?.response?.data?.message || 'Failed to remove admin. Please try again.';
    addError(errorMessage);
    return false;
  }
}

export const apiSaveLayout = async (layout: AdminDashboardLayout) => {
  try {
    const result = await api.post(`/user/ui-preferences`, layout);
    return true;
  } catch (error: any) {
    console.error(error);
    const errorMessage = error?.response?.data?.message || 'Failed to save layout. Please try again.';
    addError(errorMessage);
    return false;
  }
}